import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { PackagePlus, Check, X, SplitSquareHorizontal, Store, Printer } from 'lucide-react';
import api from '@/lib/api';
import { printBranchOrderSlip } from '@/lib/printBranchOrder';
import { toast } from 'sonner';

// Reject reasons reused from the standard reason-selection pattern.
const REJECT_REASONS = ['Out of stock', 'Insufficient quantity', 'Item discontinued', 'Wrong branch', 'Other'];

function resolveBranchCtx() {
  // iter956 — resolve the POS's ACTIVE branch + role from every possible
  // source so a stock request reaches the fulfilling branch even when the
  // logged-in user has no home branch assigned. Order: explicit POS-selected
  // branch → cashier session → portal user.
  let role = null;
  let branchId = null;
  try {
    branchId = localStorage.getItem('pos_active_branch_id')
      || localStorage.getItem('selected_branch_id')
      || null;
  } catch { /* ignore */ }
  for (const key of ['cashier_user', 'user']) {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (!role) role = parsed?.role || null;
        if (!branchId && parsed?.branch_id) branchId = parsed.branch_id;
      }
    } catch { /* ignore */ }
  }
  return { branchId, role };
}

const MULTI_BRANCH_ROLES = new Set(['admin', 'super_admin', 'owner', 'manager']);

// iter957 — short two-tone notification chime via Web Audio (no asset needed).
// Best-effort: browsers may block until the first user gesture — the POS has
// always been interacted with by the time a request lands, so it plays fine.
function playStockChime() {
  try {
    if (localStorage.getItem('pos_stock_chime_muted') === '1') return;  // iter958 — per-device mute
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    [[880, 0], [1174.7, 0.14]].forEach(([freq, at]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(0.25, now + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.28);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + at);
      osc.stop(now + at + 0.3);
    });
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 800);
  } catch { /* audio unavailable — silent */ }
}

/**
 * Live inter-branch stock-request popup. Listens on the shared `ws:dashboard`
 * event bus (same one Live Orders use) for `stock_request` events targeted at
 * THIS branch, then lets the fulfilling branch Accept / Partial Accept / Reject
 * — reusing the /stock-transfers approve + reject endpoints.
 */
export function StockRequestPopup({ branchId } = {}) {
  const [queue, setQueue] = useState([]);
  const [active, setActive] = useState(null);   // request currently shown
  const [lines, setLines] = useState([]);       // editable qty lines for partial accept
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);
  const ctx = resolveBranchCtx();
  // Prop wins (POS passes its active branch); else the resolved session branch.
  const myBranch = branchId || ctx.branchId;
  const isMultiBranch = MULTI_BRANCH_ROLES.has((ctx.role || '').toLowerCase());

  const enqueue = useCallback((ev) => {
    // iter956 — Show the request when it targets THIS branch, OR when we can't
    // confidently pin the branch (unassigned user), OR the viewer is a
    // multi-branch role (manager/admin/owner) who may act for any branch.
    // This guarantees branches like ISL still get the popup even if the
    // logged-in user isn't formally assigned to them.
    // Only the supplying branch gets an actionable popup; server enforces the same.
    if (myBranch ? ev.target_branch_id !== myBranch : !isMultiBranch) return;
    setQueue((q) => {
      if (q.some((x) => x.transfer_id === ev.transfer_id)) return q;
      playStockChime();  // iter957 — audible alert for a new cross-branch request
      return [...q, ev];
    });
  }, [myBranch, isMultiBranch]);

  useEffect(() => {
    const onBus = (e) => {
      const ev = e?.detail;
      if (ev && ev.type === 'stock_request' && ev.transfer_id) enqueue(ev);
    };
    window.addEventListener('ws:dashboard', onBus);
    return () => window.removeEventListener('ws:dashboard', onBus);
  }, [enqueue]);

  // Promote the next queued request into the modal when idle.
  useEffect(() => {
    if (!active && queue.length) {
      const next = queue[0];
      setActive(next);
      setLines((next.items || []).map((i) => ({ ...i, approved_qty: i.qty })));
      setRejecting(false);
      setRejectReason('');
      setQueue((q) => q.slice(1));
    }
  }, [queue, active]);

  const close = () => { setActive(null); setRejecting(false); setRejectReason(''); };

  // Reuse the shared internal (non-tax) branch-order slip printer.
  const printSlip = () => {
    if (!active) return;
    printBranchOrderSlip({
      requestNumber: active.request_number,
      // slip uses physical transfer semantics: From = source (fulfils), To = requester.
      fromBranch: active.fulfilling_branch_name,
      toBranch: active.from_branch_name,
      dateTime: active.requested_at,
      status: 'pending',
      items: (active.items || []).map((i) => ({ name: i.item, qty: i.qty, unit: i.unit })),
      notes: active.reason,
    });
  };

  const submitApprove = async (partial) => {
    if (!active) return;
    setBusy(true);
    try {
      const items = lines.map((l) => ({
        item_id: l.item_id, item_name: l.item, unit: l.unit,
        approved_qty: parseFloat(l.approved_qty) || 0,
      }));
      const r = await api.put(`/cashier-stock-transfers/${active.transfer_id}/approve`, { items });
      toast.success(partial ? 'Partially accepted & recorded to ledger' : 'Stock request accepted', {
        description: r.data?.transfer_value ? `Ledger credit: ${r.data.transfer_value}` : undefined,
      });
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not accept request');
    } finally { setBusy(false); }
  };

  const submitReject = async () => {
    if (!active) return;
    setBusy(true);
    try {
      await api.put(`/cashier-stock-transfers/${active.transfer_id}/reject`, { reason: rejectReason || 'Rejected' });
      toast.success('Stock request rejected');
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reject request');
    } finally { setBusy(false); }
  };

  if (!active) return null;

  return (
    <Dialog open={!!active} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-md" data-testid="stock-request-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-outfit">
            <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
              <PackagePlus size={16} className="text-white" />
            </span>
            Incoming Stock Request
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 text-sm">
          <Store size={14} className="text-muted-foreground" />
          <span className="text-muted-foreground">From</span>
          <Badge variant="outline" className="font-semibold" data-testid="stock-request-from-branch">
            {active.from_branch_name || 'Branch'}
          </Badge>
          {active.requested_by_name && <span className="text-xs text-muted-foreground">· {active.requested_by_name}</span>}
        </div>

        {(active.request_number || active.requested_at) && (
          <div className="flex items-center justify-between text-[11px] text-muted-foreground" data-testid="stock-request-meta">
            {active.request_number && <span className="font-mono" data-testid="stock-request-number">#{active.request_number}</span>}
            {active.requested_at && <span>{new Date(active.requested_at).toLocaleString('en-GB')}</span>}
          </div>
        )}

        <div className="space-y-2 mt-1" data-testid="stock-request-items">
          {lines.map((l, i) => (
            <div key={i} className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg" data-testid={`stock-request-item-${i}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{l.item}</p>
                <p className="text-[10px] text-muted-foreground">Requested: {l.qty} {l.unit || 'pc'}</p>
              </div>
              <div className="w-24">
                <Label className="text-[10px]">Approve Qty</Label>
                <Input
                  type="number" min="0" value={l.approved_qty}
                  onChange={(e) => { const u = [...lines]; u[i].approved_qty = e.target.value; setLines(u); }}
                  className="h-8 text-xs" data-testid={`stock-request-qty-${i}`}
                />
              </div>
            </div>
          ))}
        </div>

        {active.reason && <p className="text-xs text-muted-foreground">Reason: {active.reason}</p>}

        {!rejecting && (
          <Button variant="outline" size="sm" className="rounded-xl text-xs h-8 w-full" onClick={printSlip} data-testid="stock-request-print">
            <Printer size={14} className="mr-1.5" /> Print Order Slip
          </Button>
        )}

        {rejecting ? (
          <div className="space-y-2">
            <Label className="text-xs">Reason for rejection</Label>
            <div className="flex flex-wrap gap-1.5">
              {REJECT_REASONS.map((r) => (
                <button key={r} type="button" onClick={() => setRejectReason(r)}
                  className={`text-xs px-2 py-1 rounded-full border transition-colors ${rejectReason === r ? 'bg-error/10 border-error/40 text-error' : 'border-input hover:bg-accent'}`}
                  data-testid={`reject-reason-${r.replace(/\s+/g, '-').toLowerCase()}`}>
                  {r}
                </button>
              ))}
            </div>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Add details…" className="h-16 text-sm" data-testid="stock-request-reject-note" />
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setRejecting(false)} disabled={busy}>Back</Button>
              <Button className="bg-error hover:bg-error" onClick={submitReject} disabled={busy} data-testid="stock-request-confirm-reject">
                <X size={14} className="mr-1" /> Confirm Reject
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <DialogFooter className="grid grid-cols-3 gap-2">
            <Button variant="outline" className="border-error/30 text-error hover:bg-error/10" onClick={() => setRejecting(true)} disabled={busy} data-testid="stock-request-reject">
              <X size={14} className="mr-1" /> Reject
            </Button>
            <Button variant="outline" className="border-info/40 text-info hover:bg-info/10" onClick={() => submitApprove(true)} disabled={busy} data-testid="stock-request-partial">
              <SplitSquareHorizontal size={14} className="mr-1" /> Partial
            </Button>
            <Button className="bg-success hover:bg-success" onClick={() => submitApprove(false)} disabled={busy} data-testid="stock-request-accept">
              <Check size={14} className="mr-1" /> Accept
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default StockRequestPopup;
